import React, { useEffect, useState } from "react"
import { ReactSVG } from "react-svg"
import { graphql } from "gatsby"
import { FooterFieldsFragment, FooterMenuFieldsFragment } from "../../../../graphql-types"
import * as styles from "./footer.module.scss"
import { useIsMobile } from "../../../hooks"
import FooterBarMenu from "./footer-bar-menu/footer-bar-menu"
import FooterTableMenu from "./footer-table-menu/footer-table-menu"
import {
  arrowIcon,
  facebookLogo,
  keolisLogoSmall,
  instagramLogo,
  linkedInLogo,
  twitterLogo,
  youtubeLogo,
  whatsappLogo,
} from "../../../../static/images"
import { configureDecorativeSVGAccessibility, configureSVGAccessibility } from "../../../utils/accessibility"

type RenderProps = {
  data: FooterFieldsFragment & {
    facebookAlt?: string | null
    twitterAlt?: string | null
    youtubeAlt?: string | null
    linkedinAlt?: string | null
    instagramAlt?: string | null
    whatsappAlt?: string | null
  }
  menuData: FooterMenuFieldsFragment
  locale: string
  defaultLocale: string
}

const Footer: React.FC<RenderProps> = ({ data, menuData, locale, defaultLocale }) => {
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(!isMobile)

  useEffect(() => setIsOpen(!isMobile), [isMobile])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      setIsOpen(!isOpen)
    }
  }

  return (
    <footer className={styles.globalFooter}>
      <div className={styles.collapsibleFooter}>
        <div className={styles.collapsibleFooterLogos}>
          <img
            src={data.subsidiaryLogo?.url || keolisLogoSmall}
            alt={data.subsidiaryLogo?.alt || ""}
            className={styles.keolisLogo}
            width={data.subsidiaryLogo?.width /* 289 */}
            height={data.subsidiaryLogo?.height /* 59 */}
          />
          <div className={styles.transportIcons}>
            {data.transportMethodsLink.map(transportMethod => {
              return transportMethod.pictogram.format === "svg" ? (
                <ReactSVG
                  src={transportMethod.pictogram.url}
                  className={styles.svgImage}
                  wrapper="span"
                  beforeInjection={svg => configureSVGAccessibility(svg, transportMethod.pictogram.alt)}
                  key={transportMethod.id}
                />
              ) : (
                <img
                  src={transportMethod.pictogram.url}
                  alt={transportMethod.pictogram.alt || ""}
                  className={styles.transportIcon}
                  key={transportMethod.id}
                />
              )
            })}
          </div>
          <ul className={styles.socialNetworksIcons}>
            {data.facebookUrl && (
              <li>
                <a href={data.facebookUrl} target="_blank" rel="noopener noreferrer">
                  <img src={facebookLogo} alt={data.facebookAlt || ""} className={styles.socialNetworkIcon} />
                </a>
              </li>
            )}
            {data.twitterUrl && (
              <li>
                <a href={data.twitterUrl} target="_blank" rel="noopener noreferrer">
                  <img src={twitterLogo} alt={data.twitterAlt || ""} className={styles.socialNetworkIcon} />
                </a>
              </li>
            )}
            {data.youtubeUrl && (
              <li>
                <a href={data.youtubeUrl} target="_blank" rel="noopener noreferrer">
                  <img src={youtubeLogo} alt={data.youtubeAlt || ""} className={styles.socialNetworkIcon} />
                </a>
              </li>
            )}
            {data.linkedinUrl && (
              <li>
                <a href={data.linkedinUrl} target="_blank" rel="noopener noreferrer">
                  <img src={linkedInLogo} alt={data.linkedinAlt || ""} className={styles.socialNetworkIcon} />
                </a>
              </li>
            )}
            {data.instagramUrl && (
              <li>
                <a href={data.instagramUrl} target="_blank" rel="noopener noreferrer">
                  <img src={instagramLogo} alt={data.instagramAlt || ""} className={styles.socialNetworkIcon} />
                </a>
              </li>
            )}
            {data.whatsappUrl && (
              <li>
                <a href={data.whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <img src={whatsappLogo} alt={data.whatsappAlt || ""} className={styles.socialNetworkIcon} />
                </a>
              </li>
            )}
          </ul>
        </div>
        {isMobile && (
          <button
            type="button"
            className={isOpen ? styles.arrowBoxOpen : styles.arrowBox}
            onClick={() => setIsOpen(!isOpen)}
            onKeyDown={handleKeyDown}
            aria-expanded={isOpen}
            aria-label={menuData.footerToggleAriaLabel}
          >
            <ReactSVG
              src={arrowIcon}
              className={styles.arrowIcon}
              aria-hidden="true"
              wrapper="span"
              beforeInjection={configureDecorativeSVGAccessibility}
            />
          </button>
        )}
      </div>
      <div className={isOpen ? styles.collapsibleContentOpen : styles.collapsibleContent}>
        <div className={`${styles.organizingAuthorityLogoBox} ${styles.hideOnMobile}`}>
          {data.organizingAuthorityTitle && (
            <div className={styles.organizingAuthorityTitle}>{data.organizingAuthorityTitle}</div>
          )}
          {data.organizingAuthorityLogo && (
            <img
              src={data.organizingAuthorityLogo.url}
              alt={data.organizingAuthorityLogo.alt}
              className={styles.organizingAuthorityLogo}
            />
          )}
        </div>
        <FooterTableMenu data={menuData} locale={locale} defaultLocale={defaultLocale} />
        <FooterBarMenu barMenu={data.barMenu} locale={locale} defaultLocale={defaultLocale} />
        <div className={`${styles.organizingAuthorityLogoBox} ${styles.showOnMobile}`}>
          {data.organizingAuthorityTitle && (
            <div className={styles.organizingAuthorityTitle}>{data.organizingAuthorityTitle}</div>
          )}
          {data.organizingAuthorityLogo && (
            <img
              src={data.organizingAuthorityLogo.url}
              alt={data.organizingAuthorityLogo.alt}
              className={styles.organizingAuthorityLogo}
            />
          )}
        </div>
      </div>
    </footer>
  )
}

export const footerFragment = graphql`
  fragment FooterFields on DatoCmsFooter {
    facebookUrl
    facebookAlt
    twitterUrl
    twitterAlt
    linkedinUrl
    linkedinAlt
    youtubeUrl
    youtubeAlt
    instagramUrl
    instagramAlt
    whatsappUrl
    whatsappAlt
    transportMethodsLink {
      id
      name
      pictogram {
        alt
        url
        format
      }
    }
    organizingAuthorityTitle
    organizingAuthorityLogo {
      alt
      url
    }
    subsidiaryLogo {
      alt
      url
      format
      width
      height
    }
    barMenu {
      ...FooterBarMenuFields
    }
  }

  fragment FooterMenuFields on DatoCmsMenu {
    footerToggleAriaLabel
    menuSection {
      label
      externalPage
      page {
        slug
      }
      headerDisplay
      footerDisplay
      subMenu {
        label
        externalPage
        page {
          slug
        }
        headerDisplay
        footerDisplay
      }
    }
  }
`
export default Footer
